import { useEffect, useMemo, useRef, useState, useSyncExternalStore, Fragment, type ReactNode } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  BellIcon,
  BuildingIcon,
  CaretDownIcon,
  DotsThreeOutlineIcon,
  GraduationCapIcon,
  HandshakeIcon,
  QuestionIcon,
  SidebarSimpleIcon,
  GearIcon,
  Icon,
  SignOutIcon,
  XIcon
} from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';
import useAuth from 'src/hooks/useAuth';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { isWikiDocumentResponse, type DocumentWithExtras } from 'src/features/wiki/types/document';
import { toggleMobileMenu } from 'src/redux/ui';
import { usePostHog } from 'src/lib/posthog';
import { useIsFreemiumUser } from 'src/hooks/useIsFreemiumUser';
import { useTenantFeature } from 'src/hooks/useTenantFeature';
import { getTenantFromPath, rememberActiveTenantCode, resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import { NON_GUEST_TENANT_MEMBERSHIP_ROLES } from 'src/types/auth';
import logo from 'src/assets/icons/icon.png';
import PremiumUpsellModal from 'src/components/ui/PremiumUpsellModal';
import { CountBadge } from 'src/components/ui/CountBadge';
import Popover from 'src/components/ui/Popover';
import { Menu, MenuItem } from 'src/components/ui/Menu';
import { getNotifications, markNotificationsAsRead } from 'src/features/notifications/redux';
import { confirmDiscardUnsavedChanges } from 'src/features/chat/hooks/useCanvasEditor';
import { isChatStreamRunning } from 'src/features/chat/chatStreamGate';
import { toast } from 'src/components/ui/toast';
import TenantSwitcher from './TenantSwitcher';
import { getMenuItems, type NavMenuItem } from './menuItems';
import { NAVBAR_REFRESH_COLLAPSED_WIDTH_CLASS, NAVBAR_REFRESH_EXPANDED_WIDTH_CLASS } from './constants';

const PRIMARY_NAVIGATION_KEYS = [
  'home',
  'chat',
  'groups',
  'actions',
  'dashboard',
  'document-hub',
  'regulatory-wiki',
  'modules'
];
const NAV_ICON_SIZE = 20;
const NAVBAR_FOOTER_HEIGHT_CLASS = 'h-[200px]';
const NAVBAR_BUTTON_HEIGHT_CLASS = 'h-9';
const NAVBAR_BUTTON_GAP_CLASS = 'gap-0.5';
const OTHER_EXPANDED_STORAGE_KEY = 'navbar-refresh-other-expanded';

const formatNotificationDate = (timestamp?: string) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const today = new Date();
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  ) {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
  }

  const daysAgo = Math.max(1, Math.floor((todayStart.getTime() - dateStart.getTime()) / (1000 * 60 * 60 * 24)));
  return daysAgo < 7 ? `${daysAgo}d` : `${Math.floor(daysAgo / 7)}w`;
};

type NavbarNotification = {
  id: string;
  title: string;
  content?: string;
  message?: string;
  timestamp?: string;
  created_at?: string;
  status?: string;
};
type ModuleLink = { title: string; path: string; icon: Icon };
type RefreshMenuItem = NavMenuItem & {
  key: string;
  displayLabel: string;
  isPrimaryNavigation: boolean;
  isHidden: boolean;
};

const REFRESH_MENU_METADATA: Record<
  string,
  Pick<RefreshMenuItem, 'key' | 'displayLabel' | 'isPrimaryNavigation' | 'isHidden'>
> = {
  '/home': { key: 'home', displayLabel: 'Home', isPrimaryNavigation: true, isHidden: false },
  '/chat': { key: 'chat', displayLabel: 'Chat', isPrimaryNavigation: true, isHidden: false },
  '/write': { key: 'write', displayLabel: 'Write', isPrimaryNavigation: false, isHidden: false },
  '/review': { key: 'review', displayLabel: 'Review', isPrimaryNavigation: false, isHidden: false },
  '/groups': { key: 'groups', displayLabel: 'Groups', isPrimaryNavigation: true, isHidden: false },
  '/actions': { key: 'actions', displayLabel: 'Actions', isPrimaryNavigation: true, isHidden: false },
  '/modules': { key: 'modules', displayLabel: 'Modules', isPrimaryNavigation: true, isHidden: false },
  '/documents': { key: 'document-hub', displayLabel: 'Document Hub', isPrimaryNavigation: true, isHidden: false },
  '/wiki': { key: 'regulatory-wiki', displayLabel: 'Regulatory Wiki', isPrimaryNavigation: true, isHidden: false },
  '/reporting': { key: 'dashboard', displayLabel: 'Dashboards', isPrimaryNavigation: true, isHidden: false },
  '/tutorials': {
    key: 'tutorials',
    displayLabel: 'Tutorials & Documentation',
    isPrimaryNavigation: false,
    isHidden: true
  },
  'https://forum.complimind.co.uk/': {
    key: 'forum',
    displayLabel: 'Forum',
    isPrimaryNavigation: false,
    isHidden: false
  },
  '/admin': { key: 'admin', displayLabel: 'Admin', isPrimaryNavigation: false, isHidden: false }
};

const FEATURE_DESCRIPTIONS: Record<string, string> = {
  '/write':
    'With full access to CompliMind, you can use our Writing Assistant to help draft meeting minutes, proposals, reports and any other documentation you need.',
  '/review':
    'With full access to CompliMind, you can use our Review Assistant to automatically check your policy documents for compliance against HTMs and other regulatory standards.',
  '/wiki':
    'With full access to CompliMind, you can explore our comprehensive Regulatory Wiki containing all regulatory documents - from national standards to your local policies.',
  '/groups':
    'With full access to CompliMind, you can use our Action Tracker to manage and track compliance actions across your organisation.',
  '/actions':
    'With full access to CompliMind, you can use our Action Tracker to manage and track compliance actions across your organisation.',
  '/reporting':
    'With full access to CompliMind, you can generate comprehensive assurance reports and compliance dashboards for your organisation.'
};

const isNavPathActive = (pathname: string, path: string) => {
  const bare = path.replace(/\/+$/, '') || '/';
  return pathname === bare || pathname.startsWith(`${bare}/`);
};

/** Document Hub files are opened at /wiki/:id. Highlight the section that owns the document. */
const navPathnameForDocument = (
  pathname: string,
  search: string,
  document: DocumentWithExtras | null,
  tenantCode: string | null
) => {
  const parts = pathname.split('/').filter(Boolean);
  const wikiIndex = parts.indexOf('wiki');
  if (wikiIndex < 0 || parts.length !== wikiIndex + 2) return pathname;

  const routeId = parts[wikiIndex + 1];
  const section = (name: string) => toTenantPath(`/${name}`, tenantCode);
  const returnTo = new URLSearchParams(search).get('return_to') ?? '';
  if (returnTo.startsWith('/documents')) return section('documents');
  if (returnTo.startsWith('/actions')) return section('actions');
  if (returnTo.startsWith('/groups')) return section('groups');

  const loaded = document?.document;
  const matchesRoute = !!loaded && (loaded.id === routeId || loaded.filestem === routeId);
  if (!matchesRoute) return pathname;
  return isWikiDocumentResponse(document) ? section('wiki') : section('documents');
};

const getRefreshMenuItem = (item: NavMenuItem): RefreshMenuItem => {
  const metadata = REFRESH_MENU_METADATA[item.path] ?? {
    key: `path:${item.path}`,
    displayLabel: item.title,
    isPrimaryNavigation: false,
    isHidden: false
  };

  return { ...item, ...metadata };
};

const subscribeMdNav = (onChange: () => void) => {
  window.addEventListener('resize', onChange);
  return () => window.removeEventListener('resize', onChange);
};

const useDesktopNav = () =>
  useSyncExternalStore(
    subscribeMdNav,
    () => window.matchMedia('(min-width: 768px)').matches,
    () => true
  );

const useSessionStorageBoolean = (storageKey: string, defaultValue: boolean) => {
  const [value, setValue] = useState(() => {
    if (typeof window === 'undefined') return defaultValue;
    const storedValue = window.sessionStorage.getItem(storageKey);
    return storedValue === null ? defaultValue : storedValue === 'true';
  });

  useEffect(() => {
    window.sessionStorage.setItem(storageKey, String(value));
  }, [storageKey, value]);

  return [value, setValue] as const;
};

type NavbarButtonBaseProps = {
  icon: React.ElementType;
  label: string;
  isActive?: boolean;
  isDisabled?: boolean;
  endAdornment?: ReactNode;
  iconContent?: ReactNode;
  className?: string;
  isCollapsed?: boolean;
  ariaExpanded?: boolean;
};

type NavbarButtonProps =
  | (NavbarButtonBaseProps & { kind: 'internal'; to: string; onClick?: (event: React.MouseEvent) => void })
  | (NavbarButtonBaseProps & {
      kind: 'external';
      href: string;
      target?: string;
      onClick?: (event: React.MouseEvent) => void;
    })
  | (NavbarButtonBaseProps & { kind: 'action'; onClick: (event: React.MouseEvent) => void });

const NavbarButton = (props: NavbarButtonProps) => {
  const {
    icon: Icon,
    label,
    isActive = false,
    isDisabled = false,
    endAdornment,
    iconContent,
    className: additionalClassName,
    isCollapsed = false,
    ariaExpanded
  } = props;
  const className = twMerge(
    isCollapsed
      ? `group relative flex ${NAVBAR_BUTTON_HEIGHT_CLASS} w-9 items-center justify-center rounded-md transition-colors`
      : `flex ${NAVBAR_BUTTON_HEIGHT_CLASS} items-center gap-2.5 rounded-md px-2 text-sm transition-colors`,
    isActive ? 'bg-primary-lightblue text-secondary-blue font-semibold' : 'text-white hover:bg-white/10',
    isDisabled && 'opacity-50',
    additionalClassName
  );
  const content = (
    <>
      {iconContent ?? <Icon size={NAV_ICON_SIZE} weight={isActive ? 'regular' : 'light'} />}
      {!isCollapsed && <span>{label}</span>}
      {!isCollapsed && endAdornment}
      {isCollapsed && (
        <span className="pointer-events-none absolute left-full top-1/2 z-[1000] ml-3 -translate-y-1/2 whitespace-nowrap rounded bg-gray-700 px-2 py-1 text-xs text-white opacity-0 transition-opacity before:absolute before:left-[-5px] before:top-1/2 before:-translate-y-1/2 before:border-y-[5px] before:border-y-transparent before:border-r-[5px] before:border-r-gray-700 group-hover:opacity-100">
          {label}
        </span>
      )}
    </>
  );

  if (props.kind === 'external') {
    return (
      <a
        href={props.href}
        target={props.target ?? '_blank'}
        rel="noopener noreferrer"
        className={className}
        onClick={props.onClick}
      >
        {content}
      </a>
    );
  }

  if (props.kind === 'internal') {
    return (
      <RouterLink to={props.to} className={className} onClick={props.onClick} aria-label={label}>
        {content}
      </RouterLink>
    );
  }

  return (
    <button type="button" className={className} onClick={props.onClick} aria-expanded={ariaExpanded} aria-label={label}>
      {content}
    </button>
  );
};

const NavButton = ({
  item,
  isActive,
  isFreemiumUser,
  onPremiumClick,
  onNavigate,
  isCollapsed = false
}: {
  item: RefreshMenuItem;
  isActive: boolean;
  isFreemiumUser: boolean;
  onPremiumClick: (path: string) => void;
  onNavigate?: (item: RefreshMenuItem) => void;
  isCollapsed?: boolean;
}) => {
  const isPremiumLocked = isFreemiumUser && item.isPremium;
  const handleClick = (event: React.MouseEvent) => {
    if (isPremiumLocked) {
      event.preventDefault();
      onPremiumClick(item.path);
      return;
    }
    if (item.key === 'chat' && isChatStreamRunning()) {
      event.preventDefault();
      toast.error('Please wait for the current chat to complete before starting a new one.');
      return;
    }
    if (item.key === 'chat' && !confirmDiscardUnsavedChanges()) {
      event.preventDefault();
      return;
    }
    item.onBeforeNavigate?.();
    onNavigate?.(item);
  };

  return (
    <NavbarButton
      icon={item.icon}
      label={item.displayLabel}
      isActive={isActive}
      isDisabled={isPremiumLocked}
      isCollapsed={isCollapsed}
      {...(item.path.startsWith('http')
        ? { kind: 'external' as const, href: item.path, target: item.target, onClick: handleClick }
        : { kind: 'internal' as const, to: item.path, onClick: handleClick })}
    />
  );
};

const NotificationsContent = ({
  notifications,
  isLoading
}: {
  notifications: NavbarNotification[];
  isLoading: boolean;
}) => (
  <div className="custom-scrollbar max-h-96 w-80 overflow-y-auto p-3">
    {isLoading ? (
      <div className="text-sm text-content-subtle">Loading notifications...</div>
    ) : notifications.length === 0 ? (
      <div className="text-center text-sm text-content-subtle">No notifications</div>
    ) : (
      notifications.map((notification, index) => (
        <div key={notification.id} className={twMerge(index === 0 ? 'pb-3' : 'border-t border-border-subtle py-3')}>
          <div className="flex items-start gap-3">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border-subtle p-1">
              <img src={logo} className="h-5 w-5 object-contain" alt="" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm font-semibold leading-5 text-content-default">{notification.title}</div>
                <time className="shrink-0 pt-0.5 text-xs leading-4 text-content-subtlest">
                  {formatNotificationDate(notification.timestamp || notification.created_at)}
                </time>
              </div>
              <div className="mt-1 text-sm leading-5 text-content-subtle">
                {notification.content || notification.message}
              </div>
            </div>
          </div>
        </div>
      ))
    )}
  </div>
);

const ModuleNavigation = ({
  isCollapsed,
  canShowModules,
  moduleLinks,
  tenantCode,
  pathname
}: {
  isCollapsed: boolean;
  canShowModules: boolean;
  moduleLinks: ModuleLink[];
  tenantCode: string | null;
  pathname: string;
}) => {
  if (!canShowModules) return null;

  const moduleTo = (path: string) => (tenantCode ? toTenantPath(path, tenantCode) : path);

  return (
    <>
      {moduleLinks.map((module) => {
        const to = moduleTo(module.path);
        return (
          <NavbarButton
            key={module.path}
            kind="internal"
            icon={module.icon}
            label={module.title}
            to={to}
            isActive={isNavPathActive(pathname, to)}
            isCollapsed={isCollapsed}
          />
        );
      })}
    </>
  );
};

const OtherNavigation = ({
  isCollapsed,
  otherItems,
  pathname,
  isFreemiumUser,
  otherExpanded,
  otherButtonRef,
  onPremiumClick,
  onToggleOther,
  onToggleCollapsedPopover
}: {
  isCollapsed: boolean;
  otherItems: RefreshMenuItem[];
  pathname: string;
  isFreemiumUser: boolean;
  otherExpanded: boolean;
  otherButtonRef: React.RefObject<HTMLButtonElement | null>;
  onPremiumClick: (path: string) => void;
  onToggleOther: () => void;
  onToggleCollapsedPopover: (popover: 'other') => void;
}) =>
  isCollapsed ? (
    <button
      ref={otherButtonRef}
      type="button"
      onClick={() => onToggleCollapsedPopover('other')}
      className={`group relative flex ${NAVBAR_BUTTON_HEIGHT_CLASS} w-9 items-center justify-center rounded-md text-white hover:bg-white/10`}
      aria-label="Other navigation"
    >
      <DotsThreeOutlineIcon size={NAV_ICON_SIZE} weight="light" />
      <span className="pointer-events-none absolute left-full top-1/2 z-[1000] ml-3 -translate-y-1/2 whitespace-nowrap rounded bg-gray-700 px-2 py-1 text-xs text-white opacity-0 transition-opacity before:absolute before:left-[-5px] before:top-1/2 before:-translate-y-1/2 before:border-y-[5px] before:border-y-transparent before:border-r-[5px] before:border-r-gray-700 group-hover:opacity-100">
        Other
      </span>
    </button>
  ) : (
    <>
      <NavbarButton
        kind="action"
        icon={DotsThreeOutlineIcon}
        label="Other"
        onClick={onToggleOther}
        className="w-full"
        ariaExpanded={otherExpanded}
        endAdornment={
          <CaretDownIcon
            size={16}
            weight="light"
            className={twMerge('ml-auto transition-transform', otherExpanded ? 'rotate-0' : '-rotate-90')}
          />
        }
      />
      {otherExpanded && (
        <div className="ml-3 mt-1 flex flex-col gap-1 border-l-2 border-white/20 px-1">
          {otherItems.map((item) => (
            <NavButton
              key={item.key}
              item={item}
              isActive={isNavPathActive(pathname, item.path)}
              isFreemiumUser={isFreemiumUser}
              onPremiumClick={onPremiumClick}
            />
          ))}
        </div>
      )}
    </>
  );

const NavigationContent = ({
  isCollapsed,
  visibleItems,
  pathname,
  isFreemiumUser,
  onPremiumClick,
  onNavigate,
  afterActions,
  children
}: {
  isCollapsed: boolean;
  visibleItems: RefreshMenuItem[];
  pathname: string;
  isFreemiumUser: boolean;
  onPremiumClick: (path: string) => void;
  onNavigate?: (item: RefreshMenuItem) => void;
  afterActions?: ReactNode;
  children: ReactNode;
}) => (
  <div className={`flex flex-col ${NAVBAR_BUTTON_GAP_CLASS}`}>
    {visibleItems.map((item) => (
      <Fragment key={item.key}>
        <NavButton
          item={item}
          isActive={isNavPathActive(pathname, item.path)}
          isFreemiumUser={isFreemiumUser}
          onPremiumClick={onPremiumClick}
          onNavigate={onNavigate}
          isCollapsed={isCollapsed}
        />
        {item.key === 'actions' ? afterActions : null}
      </Fragment>
    ))}
    {children}
  </div>
);

const NavbarHeader = ({
  isCollapsed,
  tenantCode,
  unreadNotificationCount,
  notifications,
  notificationsLoading,
  onNotificationsOpenChange,
  onToggleCollapsed,
  toggleMode = 'collapse'
}: {
  isCollapsed: boolean;
  tenantCode: string | null;
  unreadNotificationCount: number;
  notifications: NavbarNotification[];
  notificationsLoading: boolean;
  onNotificationsOpenChange: (open: boolean) => void;
  onToggleCollapsed: () => void;
  toggleMode?: 'collapse' | 'close';
}) => {
  const [notificationsAnchor, setNotificationsAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!isCollapsed) return;
    setNotificationsAnchor(null);
    onNotificationsOpenChange(false);
  }, [isCollapsed, onNotificationsOpenChange]);

  return (
    <header
      className={twMerge('flex h-14 shrink-0 items-center border-b border-white/20', isCollapsed ? 'px-2' : 'px-3')}
    >
      <div
        className={twMerge(
          `flex ${NAVBAR_BUTTON_HEIGHT_CLASS} w-full items-center`,
          isCollapsed ? 'justify-start' : 'justify-between'
        )}
      >
        <RouterLink
          to={tenantCode ? toTenantPath('/home', tenantCode) : '/home'}
          className={twMerge('flex h-full min-w-0 items-center gap-2 pl-1', isCollapsed && 'hidden')}
        >
          <img src={logo} className="block h-7 shrink-0 object-contain" alt="CompliMind" />
        </RouterLink>
        <div className="flex shrink-0 items-center">
          {isCollapsed ? null : (
            <button
              type="button"
              onClick={(event) => {
                const anchor = event.currentTarget;
                const next = notificationsAnchor ? null : anchor;
                setNotificationsAnchor(next);
                onNotificationsOpenChange(next != null);
              }}
              className={`group relative flex ${NAVBAR_BUTTON_HEIGHT_CLASS} w-9 items-center justify-center rounded-md text-white hover:bg-white/10`}
              aria-label="Notifications"
              aria-expanded={notificationsAnchor != null}
            >
              <BellIcon size={NAV_ICON_SIZE} />
              {unreadNotificationCount > 0 ? (
                <CountBadge
                  count={unreadNotificationCount}
                  className="absolute right-0 top-0 !w-auto min-w-4 bg-cyan-400 px-1 font-sans text-secondary-blue"
                />
              ) : null}
            </button>
          )}
          <Popover
            open={notificationsAnchor != null}
            anchorEl={notificationsAnchor}
            onClose={() => {
              setNotificationsAnchor(null);
              onNotificationsOpenChange(false);
            }}
            placement="bottom-start"
          >
            <NotificationsContent notifications={notifications} isLoading={notificationsLoading} />
          </Popover>
          <button
            type="button"
            onClick={onToggleCollapsed}
            className={`group relative flex ${NAVBAR_BUTTON_HEIGHT_CLASS} w-9 items-center justify-center rounded-md text-white hover:bg-white/10`}
            aria-label={toggleMode === 'close' ? 'Close menu' : isCollapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {toggleMode === 'close' ? <XIcon size={NAV_ICON_SIZE} /> : <SidebarSimpleIcon size={NAV_ICON_SIZE} />}
            {isCollapsed && unreadNotificationCount > 0 ? (
              <CountBadge
                count={unreadNotificationCount}
                className="absolute right-0 top-0 !w-auto min-w-4 bg-cyan-400 px-1 font-sans text-secondary-blue"
              />
            ) : null}
            {toggleMode === 'collapse' ? (
              <span className="pointer-events-none absolute left-full top-1/2 z-[1000] ml-3 -translate-y-1/2 whitespace-nowrap rounded bg-gray-700 px-2 py-1 text-xs text-white opacity-0 transition-opacity before:absolute before:left-[-5px] before:top-1/2 before:-translate-y-1/2 before:border-y-[5px] before:border-y-transparent before:border-r-[5px] before:border-r-gray-700 group-hover:opacity-100">
                {isCollapsed ? 'Expand menu' : 'Collapse menu'}
              </span>
            ) : null}
          </button>
        </div>
      </div>
    </header>
  );
};

const NavbarFooter = ({
  isCollapsed,
  pathname,
  tenantCode,
  onLogout,
  showContents
}: {
  isCollapsed: boolean;
  pathname: string;
  tenantCode: string | null;
  onLogout: (event: React.MouseEvent) => void;
  showContents: boolean;
}) => {
  const helpPath = tenantCode ? toTenantPath('/tutorials', tenantCode) : '/tutorials';
  const profilePath = tenantCode ? toTenantPath('/settings', tenantCode) : '/settings';

  return (
    <div
      className={twMerge(
        `flex shrink-0 flex-col justify-end border-t border-white/20 py-3 ${NAVBAR_FOOTER_HEIGHT_CLASS}`,
        'px-2',
        isCollapsed && NAVBAR_BUTTON_GAP_CLASS
      )}
    >
      <div className={showContents ? undefined : 'invisible'} aria-hidden={showContents ? undefined : true}>
        <TenantSwitcher
          variant={isCollapsed ? 'compact' : 'expanded'}
          compactSizing={isCollapsed ? 'parent' : 'fixed'}
          tone="inverse"
        />
        <div className={twMerge(`flex flex-col mt-2 ${NAVBAR_BUTTON_GAP_CLASS}`)}>
          <NavbarButton
            kind="internal"
            icon={QuestionIcon}
            label="Help & Tutorials"
            to={helpPath}
            isActive={isNavPathActive(pathname, helpPath)}
            isCollapsed={isCollapsed}
          />
          <NavbarButton
            kind="internal"
            icon={GearIcon}
            label="Settings"
            to={profilePath}
            isActive={isNavPathActive(pathname, profilePath)}
            isCollapsed={isCollapsed}
          />
          <NavbarButton
            kind="action"
            icon={SignOutIcon}
            label="Log out"
            onClick={onLogout}
            isCollapsed={isCollapsed}
            className="text-white hover:bg-white/10"
          />
        </div>
      </div>
    </div>
  );
};

export default function NavbarRefresh({
  isCollapsed,
  onToggleCollapsed
}: {
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const { pathname, search, key: locationKey } = useLocation();
  const currentDocument = useAppSelector((state) => state.wiki.currentDocument);
  const navigate = useNavigate();
  const { notifications, isLoading: notificationsLoading } = useAppSelector(
    (state) => state.notifications as { notifications: NavbarNotification[]; isLoading: boolean }
  );
  const isMobileMenuOpen = useAppSelector((state) => state.ui.isMobileMenuOpen);
  const isDesktopNav = useDesktopNav();
  const railCollapsed = isDesktopNav && isCollapsed;
  const dispatch = useAppDispatch();
  const { user, isInitialized, isPlatformAdmin: isAdmin, logout } = useAuth();
  const posthog = usePostHog();
  const isFreemiumUser = useIsFreemiumUser();
  const pfiModuleEnabled = useTenantFeature('pfi_module');
  const pamModuleEnabled = useTenantFeature('national_returns_hub');
  const trainingHubEnabled = useTenantFeature('training_hub');
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [premiumFeatureDescription, setPremiumFeatureDescription] = useState('');
  const [otherExpanded, setOtherExpanded] = useSessionStorageBoolean(OTHER_EXPANDED_STORAGE_KEY, false);
  const [collapsedPopover, setCollapsedPopover] = useState<'other' | null>(null);
  const otherButtonRef = useRef<HTMLButtonElement>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const tenantCode = resolveTenantCode(pathname, user?.tenants) || null;

  useEffect(() => {
    const fromPath = getTenantFromPath(pathname);
    if (!fromPath || !user?.tenants?.some((tenant) => tenant.code === fromPath)) return;
    rememberActiveTenantCode(fromPath);
  }, [pathname, user?.tenants]);
  const navPathname = navPathnameForDocument(pathname, search, currentDocument, tenantCode);
  const activeTenantRole = user?.getTenantRole(tenantCode ?? undefined);
  const unreadNotificationCount = notifications.filter((notification) => notification.status === 'unread').length;

  useEffect(() => {
    dispatch(getNotifications());
  }, [dispatch]);

  useEffect(() => {
    dispatch(toggleMobileMenu(false));
  }, [dispatch, pathname, locationKey]);

  useEffect(() => {
    if (isDesktopNav || !isMobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDesktopNav, isMobileMenuOpen]);

  useEffect(() => {
    if (!notificationsOpen) return;
    dispatch(getNotifications());
  }, [dispatch, notificationsOpen]);

  useEffect(() => {
    if (!notificationsOpen || notificationsLoading) return;
    const unreadIds = notifications
      .filter((notification) => notification.status === 'unread')
      .map((notification) => notification.id);
    if (unreadIds.length > 0) {
      dispatch(markNotificationsAsRead(unreadIds));
    }
  }, [dispatch, notificationsOpen, notifications, notificationsLoading]);

  const menuItems = useMemo(() => {
    const items = getMenuItems({ tenantRole: activeTenantRole, isAdmin, posthog })
      .map(getRefreshMenuItem)
      .map((item) => {
        if (tenantCode && item.tenantScoped && item.path.startsWith('/')) {
          return { ...item, path: toTenantPath(item.path, tenantCode) };
        }
        return item;
      });
    return items.sort((a, b) => {
      const aIndex = PRIMARY_NAVIGATION_KEYS.indexOf(a.key);
      const bIndex = PRIMARY_NAVIGATION_KEYS.indexOf(b.key);
      return (
        (aIndex === -1 ? PRIMARY_NAVIGATION_KEYS.length : aIndex) -
        (bIndex === -1 ? PRIMARY_NAVIGATION_KEYS.length : bIndex)
      );
    });
  }, [activeTenantRole, isAdmin, posthog, tenantCode]);

  const mobileAwareItems = isDesktopNav ? menuItems : menuItems.filter((item) => item.showOnMobile !== false);
  const visibleItems = mobileAwareItems.filter((item) => item.isPrimaryNavigation && item.key !== 'modules');
  const otherItems = mobileAwareItems.filter((item) => !item.isPrimaryNavigation && !item.isHidden);
  const canShowModuleLinks = Boolean(
    activeTenantRole &&
      NON_GUEST_TENANT_MEMBERSHIP_ROLES.includes(activeTenantRole) &&
      (isDesktopNav || mobileAwareItems.some((item) => item.key === 'modules'))
  );
  const trainingHubLink: ModuleLink | null =
    canShowModuleLinks && trainingHubEnabled
      ? { title: 'Training Hub', path: '/modules/training-hub', icon: GraduationCapIcon }
      : null;
  const moduleLinks = (
    canShowModuleLinks
      ? [
          pfiModuleEnabled && { title: 'PFI', path: '/modules/pfi', icon: HandshakeIcon },
          pamModuleEnabled && { title: 'PAM', path: '/modules/pam', icon: BuildingIcon }
        ]
      : []
  ).filter(Boolean) as ModuleLink[];
  const canShowModules = moduleLinks.length > 0;

  const handlePremiumClick = (itemPath: string) => {
    posthog?.capture('freemium_premium_feature_clicked', { feature: itemPath });
    const featurePath =
      tenantCode && itemPath.startsWith(`/${tenantCode}/`) ? itemPath.slice(tenantCode.length + 1) : itemPath;
    setPremiumFeatureDescription(FEATURE_DESCRIPTIONS[featurePath] || '');
    setPremiumModalOpen(true);
  };

  const handleLogout = async () => {
    await logout();
  };

  // Account, role, and feature flags all arrive on the user. Hold the bar's
  // contents until then so links and the organisation switcher appear together.
  const navbarReady = isInitialized && user != null;

  const handleCollapsedMenuNavigation = (item: NavMenuItem) => {
    if (isFreemiumUser && item.isPremium) {
      setCollapsedPopover(null);
      handlePremiumClick(item.path);
      return;
    }

    item.onBeforeNavigate?.();
    setCollapsedPopover(null);
    if (item.path.startsWith('http')) {
      window.open(item.path, item.target ?? '_blank');
      return;
    }
    navigate(item.path);
  };

  return (
    <>
      <aside
        className={twMerge(
          'fixed inset-y-0 left-0 flex flex-col bg-[#34416F] text-white transition-transform duration-300 ease-in-out',
          railCollapsed ? NAVBAR_REFRESH_COLLAPSED_WIDTH_CLASS : NAVBAR_REFRESH_EXPANDED_WIDTH_CLASS,
          isDesktopNav
            ? 'z-50 translate-x-0'
            : twMerge('z-[999]', isMobileMenuOpen ? 'translate-x-0' : 'pointer-events-none -translate-x-full')
        )}
      >
        <NavbarHeader
          isCollapsed={railCollapsed}
          tenantCode={tenantCode}
          unreadNotificationCount={unreadNotificationCount}
          notifications={notifications}
          notificationsLoading={notificationsLoading}
          onNotificationsOpenChange={setNotificationsOpen}
          toggleMode={isDesktopNav ? 'collapse' : 'close'}
          onToggleCollapsed={() => {
            if (!isDesktopNav) {
              dispatch(toggleMobileMenu(false));
              return;
            }
            onToggleCollapsed();
          }}
        />
        <nav
          className={twMerge(
            `flex flex-1 flex-col pt-3 ${NAVBAR_BUTTON_GAP_CLASS}`,
            railCollapsed ? 'items-start px-2' : 'min-h-0 overflow-y-auto custom-scrollbar px-2'
          )}
        >
          {navbarReady ? (
            <div className={`flex flex-col ${NAVBAR_BUTTON_GAP_CLASS}`}>
              <NavigationContent
                isCollapsed={railCollapsed}
                visibleItems={visibleItems}
                pathname={navPathname}
                isFreemiumUser={isFreemiumUser}
                onPremiumClick={handlePremiumClick}
                afterActions={
                  trainingHubLink ? (
                    <ModuleNavigation
                      isCollapsed={railCollapsed}
                      canShowModules
                      moduleLinks={[trainingHubLink]}
                      tenantCode={tenantCode}
                      pathname={navPathname}
                    />
                  ) : null
                }
              >
                <ModuleNavigation
                  isCollapsed={railCollapsed}
                  canShowModules={canShowModules}
                  moduleLinks={moduleLinks}
                  tenantCode={tenantCode}
                  pathname={navPathname}
                />
                <OtherNavigation
                  isCollapsed={railCollapsed}
                  otherItems={otherItems}
                  pathname={navPathname}
                  isFreemiumUser={isFreemiumUser}
                  otherExpanded={otherExpanded}
                  otherButtonRef={otherButtonRef}
                  onPremiumClick={handlePremiumClick}
                  onToggleOther={() => setOtherExpanded((isExpanded) => !isExpanded)}
                  onToggleCollapsedPopover={(targetPopover) =>
                    setCollapsedPopover((popover) => (popover === targetPopover ? null : targetPopover))
                  }
                />
              </NavigationContent>
            </div>
          ) : null}
        </nav>
        <Menu
          anchorEl={otherButtonRef.current}
          open={railCollapsed && collapsedPopover === 'other'}
          onClose={() => setCollapsedPopover(null)}
          placement="right-start"
        >
          {otherItems.map((item) => (
            <MenuItem
              key={item.key}
              selected={isNavPathActive(navPathname, item.path)}
              icon={item.icon}
              onClick={() => handleCollapsedMenuNavigation(item)}
            >
              {item.displayLabel}
            </MenuItem>
          ))}
        </Menu>
        <NavbarFooter
          isCollapsed={railCollapsed}
          pathname={navPathname}
          tenantCode={tenantCode}
          onLogout={handleLogout}
          showContents={navbarReady}
        />
      </aside>
      <PremiumUpsellModal
        open={premiumModalOpen}
        onClose={() => setPremiumModalOpen(false)}
        featureDescription={premiumFeatureDescription}
      />
    </>
  );
}
